<script setup lang="ts">
import {navigateTo, useRoute} from "#imports";
import {WadLockState, type WadLockStateType} from "~/types/constants";

const {
  lockState,
  title = 'Unlock full access',
  description = null,
} = defineProps<{
  lockState: WadLockStateType,
  title?: string,
  description?: string | null,
}>();

const route = useRoute()
const localePath = useLocalePath()
const {isAuthenticated, user} = useAuthUser()
const {getLoginUrl, getSignupUrl} = useWadUrls()

const requiresPro = computed(() => lockState === WadLockState.RequirePro)
const requiresAuth = computed(() => lockState === WadLockState.RequireAuth)

const buttonText = computed(() => {
  if(user.value?.pro?.can_trial) {
    return 'Start free trial';
  }
  return 'Get PRO now';
})

const lockClass = computed(() => {
  switch (lockState) {
    case WadLockState.RequireAuth:
      return 'wad4-locked-content--guest'
    case WadLockState.RequirePro:
      return 'wad4-locked-content--guest-pro'
    default:
      return ''
  }
})

const ctaUrl = computed(() => {
  if(isAuthenticated.value || requiresPro.value) {
    return `${localePath({name: 'user-wearedevelopers-pro'})}?redirect=${encodeURIComponent(route.fullPath)}`;
  }
  return getLoginUrl(route.fullPath)
})

</script>

<template>
  <div class="wad4-locked-content" :class="lockClass">
    <div v-if="requiresPro" class="wad4-locked-content__header">
      <div class="wad4-locked-content__badge">WeAreDevelopers PRO</div>
    </div>
    <div class="wad4-locked-content__body">
      <div class="wad4-locked-content__content">
        <div class="wad4-locked-content__title">{{ title }}</div>
        <div class="wad4-locked-content__info">
          <p v-if="description" class="wad4-locked-content__text">{{ description }}</p>
          <template v-else>
            <template v-if="requiresPro">
              <p class="wad4-locked-content__text">
                Get the full potential of this and other exclusive features with WeAreDevelopers Pro.
              </p>
            </template>
            <template v-if="requiresAuth">
              <p class="wad4-locked-content__text">
                Log in or <a class="wad4-locked-content__link" :href="getSignupUrl(route.fullPath)">set up an account</a> to access this feature and more.
              </p>
            </template>
          </template>
        </div>
      </div>
      <div class="wad4-locked-content__actions">
        <a class="wad4-button-primary wad4-locked-content__button" :href="ctaUrl">{{ buttonText }}</a>
        <p v-if="requiresPro && !isAuthenticated" class="wad4-locked-content__login">
          Already a PRO user? <a class="wad4-locked-content__link" :href="getLoginUrl(route.fullPath)">Log in</a>
        </p>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.wad4-locked-content {
  display: flex;
  flex-direction: column;
  border-radius: $wad4-border-radius-lg;
  border: 1px solid $wad4-c-border-border-brand-solid;
  background: $wad4-c-background-bg-primary;
  overflow: hidden;
  width: 100%;
  cursor: default;
  box-shadow: 0 10px 15px -3px $wad4-bc-shadow-neutral-10, 0 4px 6px -2px $wad4-bc-shadow-neutral-5;
  padding-bottom: $wad4-grid-margin;
  gap: $wad4-spacing-xl;

  @include mqBreakPoint(0, $wad4-breakpoint-mobile-max) {
    padding-bottom: $wad4-spacing-lg;
  }

  &--guest-pro {
    border: 1px solid $wad4-c-border-border-success;
  }

  &__header {
    padding: $wad4-spacing-2xl;
    background: $wad4-c-background-bg-success-primary;

    @include mqBreakPoint(0, $wad4-breakpoint-mobile-max) {
      padding: $wad4-spacing-lg;
    }
  }

  &__badge {
    display: flex;
    padding: $wad4-spacing-xs $wad4-spacing-md;
    align-items: center;
    gap: 8px;
    border-radius: 4px;
    border: 1px solid $wad4-c-border-border-success;
    background: $wad4-c-background-bg-success-primary;
    color: $wad4-c-text-text-success;
    @include text-size-mixin('xs');
    font-weight: $wad4-font-weight-semi-bold;
    width: fit-content;
  }

  &__body {
    display: flex;
    padding: 0 $wad4-spacing-2xl;
    justify-content: space-between;
    align-items: center;
    gap: $wad4-spacing-xl;
    background: $wad4-c-background-bg-primary;

    @include mqBreakPoint(0, $wad4-breakpoint-mobile-max) {
      flex-direction: column;
      align-items: stretch;
      padding: 0 $wad4-spacing-lg;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: $wad4-spacing-md;
    flex: 1 0 0;
  }

  &__title {
    @include text-size-mixin('xxl');
    color: $wad4-c-text-text-primary;
    font-weight: $wad4-font-weight-semi-bold;
  }

  &__info {
    @include text-size-mixin('sm');
  }

  &__text {
    color: $wad4-c-text-text-primary;
    margin: 0;
  }

  &__link {
    color: $wad4-c-text-text-primary;
    text-decoration: underline;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $wad4-spacing-lg;
    flex-shrink: 0;
    align-self: stretch;
    @include text-size-mixin('sm');

    @include mqBreakPoint(0, $wad4-breakpoint-mobile-max) {
      align-items: stretch;
    }
  }

  &__button {
    @include mqBreakPoint(0, $wad4-breakpoint-mobile-max) {
      width: 100%;
    }
  }

  &__login {
    margin: 0;
    color: $wad4-c-text-text-primary;
  }
}
</style>
